import React from 'react';
import { T } from '../tokens';
import { useTheme } from '../theme';
import { Ico } from '../icons';

export interface ChoiceChipOption {
  value: string;
  label: string;
  sub?: string;
}

export interface ChoiceChipsProps {
  value: string;
  options: ChoiceChipOption[];
  onChange: (value: string) => void;
  columns?: number;
}

export function ChoiceChips({ value, options, onChange, columns }: ChoiceChipsProps) {
  const t = useTheme();
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: columns ? `repeat(${columns},1fr)` : 'none',
      gridAutoFlow: columns ? 'row' : 'column',
      gap: 8,
    }}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button key={o.value} onClick={() => onChange(o.value)} style={{
            display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
            padding: '11px 13px', borderRadius: t.radius, cursor: 'pointer', textAlign: 'left',
            border: `1.5px solid ${on ? t.accent : T.border}`,
            background: on ? T.accentLight : T.surface,
            color: on ? t.accent : T.fg2,
            transition: 'all 140ms', fontFamily: T.font,
          }}>
            <span style={{ fontSize: 13.5, fontWeight: 700 }}>{o.label}</span>
            {o.sub && <span style={{ fontSize: 11, fontWeight: 600, color: on ? t.accent : T.fg3 }}>{o.sub}</span>}
          </button>
        );
      })}
    </div>
  );
}

export interface CounterProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
}

export function Counter({ value, onChange, min = 1, max = 1000 }: CounterProps) {
  const t = useTheme();
  const btn = (lbl: React.ReactNode, fn: () => void, dis: boolean) => (
    <button onClick={fn} disabled={dis} style={{
      width: 42, height: 42, borderRadius: t.radius - 2,
      border: `1.5px solid ${T.border}`, background: T.surface,
      color: dis ? T.fg3 : t.accent,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: dis ? 'not-allowed' : 'pointer', flexShrink: 0,
    }}>
      {lbl}
    </button>
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      {btn(<Ico.minus size={18} />, () => onChange(Math.max(min, value - 1)), value <= min)}
      <span style={{ fontSize: 18, fontWeight: 800, color: T.fg, minWidth: 28, textAlign: 'center' }}>{value}</span>
      {btn(<Ico.plus size={18} />, () => onChange(Math.min(max, value + 1)), value >= max)}
    </div>
  );
}

export interface ToggleProps {
  value: boolean;
  onChange: (value: boolean) => void;
}

export function Toggle({ value, onChange }: ToggleProps) {
  const t = useTheme();
  return (
    <button onClick={() => onChange(!value)} style={{
      width: 48, height: 28, borderRadius: 999, border: 0,
      background: value ? t.accent : T.border,
      position: 'relative', cursor: 'pointer',
      transition: 'background 160ms', flexShrink: 0,
    }}>
      <span style={{
        position: 'absolute', top: 3, left: value ? 23 : 3,
        width: 22, height: 22, borderRadius: 999,
        background: T.surface, boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        transition: 'left 160ms',
      }} />
    </button>
  );
}

export interface ImageSlotsProps {
  images: { url?: string }[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  max?: number;
}

// Placeholder upload slots — replaced with real File handling in the claims phase.
export function ImageSlots({ images, onAdd, onRemove, max = 5 }: ImageSlotsProps) {
  const t = useTheme();
  return (
    <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
      {images.map((_im, i) => (
        <div key={i} style={{
          position: 'relative', width: 72, height: 90,
          borderRadius: t.radius, overflow: 'hidden',
          border: `1px solid ${T.border}`,
          background: `repeating-linear-gradient(135deg, ${T.tertiary} 0 7px, ${T.surface} 7px 14px)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Ico.image size={22} style={{ color: T.fg3 }} />
          <button onClick={() => onRemove(i)} style={{
            position: 'absolute', top: 4, right: 4,
            width: 22, height: 22, borderRadius: 999,
            border: 0, background: T.dark, color: T.onAccent,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}>
            <Ico.x size={12} />
          </button>
        </div>
      ))}
      {images.length < max && (
        <button onClick={onAdd} style={{
          width: 72, height: 90, borderRadius: t.radius,
          border: `1.5px dashed ${T.border}`, background: T.surface, color: T.fg3,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 5, cursor: 'pointer', fontFamily: T.font,
        }}>
          <Ico.camera size={20} />
          <span style={{ fontSize: 10.5, fontWeight: 700 }}>Tilføj</span>
        </button>
      )}
    </div>
  );
}
